import React, { useEffect, useState, useMemo } from 'react';
import AuthUser from './AuthUser';
import toast, { Toaster } from 'react-hot-toast';
import moment from 'moment';
import { Link } from 'react-router-dom';
import Select from 'react-select';
import * as XLSX from 'xlsx';

export default function SalaryMasterList() {
    const { http, user } = AuthUser();
    const [salaryMasters, setSalaryMasters] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterEmpType, setFilterEmpType] = useState('');
    const [showViewModal, setShowViewModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [empTypeOptions, setEmpTypeOptions] = useState([{ value: '', label: 'All Types' }]);

    useEffect(() => {
        fetchSalaryMasters();
        fetchEmpTypes();
    }, []);

    const fetchSalaryMasters = async () => {
        setLoading(true);
        try {
            const response = await http.get(`/salary_masters?login_user=${user.id}`);
            if (response.data.success) {
                setSalaryMasters(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching salary masters:', error);
            toast.error('Error fetching salary masters');
        } finally {
            setLoading(false);
        }
    };

    const fetchEmpTypes = async () => {
        try {
            const response = await http.get(`/emp_types?login_user=${user.id}`);
            if (response.data.success) {
                setEmpTypeOptions([
                    { value: '', label: 'All Types' },
                    ...response.data.data.map(et => ({ value: et.name, label: et.name }))
                ]);
            }
        } catch (error) {
            console.error('Error fetching employee types:', error);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this record?')) {
            return;
        }
        
        try {
            const response = await http.delete(`/salary_master/${id}`);
            if (response.data.success) {
                toast.success('Record deleted successfully!');
                fetchSalaryMasters();
            } else {
                toast.error(response.data.message || 'Failed to delete');
            }
        } catch (error) {
            console.error('Error deleting:', error);
            toast.error('Error deleting record');
        }
    };

    const handleView = (item) => {
        setSelectedItem(item);
        setShowViewModal(true);
    };

    // empTypeOptions loaded from API via fetchEmpTypes

    const filteredData = useMemo(() => {
        return salaryMasters.filter(item => {
            const searchLower = searchTerm.toLowerCase();
            const matchesSearch = (
                (item.user?.name || '').toLowerCase().includes(searchLower) ||
                (item.user?.department?.name || '').toLowerCase().includes(searchLower) ||
                (item.emp_type || '').toLowerCase().includes(searchLower) ||
                (item.grade || '').toLowerCase().includes(searchLower) ||
                (item.location || '').toLowerCase().includes(searchLower) ||
                (item.designation || '').toLowerCase().includes(searchLower)
            );
            const matchesEmpType = filterEmpType === '' || item.emp_type === filterEmpType;
            return matchesSearch && matchesEmpType;
        });
    }, [salaryMasters, searchTerm, filterEmpType]);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        }).format(amount || 0);
    };

    // Calculate working duration from date of joining
    const calculateWorkingDuration = (dateOfJoin) => {
        if (!dateOfJoin) return null;
        
        const joinDate = moment(dateOfJoin);
        const today = moment();
        
        if (!joinDate.isValid() || joinDate.isAfter(today)) return null;
        
        const years = today.diff(joinDate, 'years');
        joinDate.add(years, 'years');
        
        const months = today.diff(joinDate, 'months');
        joinDate.add(months, 'months');
        
        const days = today.diff(joinDate, 'days');
        
        let parts = [];
        if (years > 0) parts.push(`${years} year${years > 1 ? 's' : ''}`);
        if (months > 0) parts.push(`${months} month${months > 1 ? 's' : ''}`);
        if (days > 0 || parts.length === 0) parts.push(`${days} day${days !== 1 ? 's' : ''}`);
        
        return {
            years,
            months,
            days,
            text: parts.join(', '),
            totalDays: moment().diff(moment(dateOfJoin), 'days')
        };
    };

    const clearFilters = () => {
        setSearchTerm('');
        setFilterEmpType('');
    };

    const handleExportExcel = () => {
        const exportData = filteredData.map((item, index) => ({
            'No': index + 1,
            'Employee Name': (item.user?.name || '') + (item.user?.last_name ? ' ' + item.user.last_name : ''),
            'Department': item.user?.department?.name || '-',
            'Designation': item.designation || '-',
            'Date of Join': item.date_of_join ? moment(item.date_of_join).format('DD-MM-YYYY') : '-',
            'DOB': item.dob ? moment(item.dob).format('DD-MM-YYYY') : '-',
            'Emp Type': item.emp_type || '-',
            'Grade': item.grade || '-',
            'Location': item.location || '-',
            'Father Name': item.father_name || '-',
            'Address': item.address || '-',
            'Qualification': item.qualification || '-',
            'Experience': item.experience || '-',
            'Bank IFSC': item.bank_ifsc || '-',
            'Account No': item.account_num || '-',
            'PF Account': item.pf_acc_num || '-',
            'ESI Number': item.esi_number || '-',
            'Old Salary': item.old_salary || 0,
            'Basic Salary (A)': item.basic_salary || 0,
            'DA % (B)': item.da_per || 0,
            'HRA % (C)': item.hra_per || 0,
            'TDS % (D)': item.tds_per || 0,
            'Gross Formula': item.formula || 'A+B+C-D',
            'Gross Salary (E)': item.gross_salary || 0,
            'PF EPS Employer (F)': item.total_pf || 0,
            'PF EPF Employer (G)': item.total_pf_epf || 0,
            'ESI Employer (H)': item.total_esi || 0,
            'ESI Employer-2 (K)': item.total_esi_2 || 0,
            'Total Salary Formula': item.total_salary_formula || '-',
            'Total Salary (I)': item.total_salary || 0,
            'Working Days': item.working_days || 30,
            'Per Day Base': item.per_day_base || 'E',
            'Per Day Salary (J)': item.per_day_salary || 0,
            'PF Employee': item.total_pf_employee || 0,
            'PF Employee-2': item.total_pf_employee_2 || 0,
            'ESI Employee': item.total_esi_employee || 0,
            'ESI Employee-2': item.total_esi_employee_2 || 0,
            'PT': item.total_pt || 0,
            'TDS Deduction': item.total_tds_deduction || 0,
            'Total Deduction': item.total_deduction || 0,
            'Net Salary': item.net_salary || 0,
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Salary Masters');
        XLSX.writeFile(wb, `SalaryMasters_${moment().format('YYYY-MM-DD')}.xlsx`);
        toast.success('Exported successfully!');
    };

    return (
        <>
            <Toaster />
            <div className="container-fluid mt-2 px-3">
                {/* Compact Header */}
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <h5 className="mb-0">Salary Master</h5>
                    <div>
                        <button className="btn btn-sm btn-success me-2" onClick={handleExportExcel}>
                            <i className="fa fa-file-excel me-1"></i>Export
                        </button>
                        <Link to="/salary_master_add" className="btn btn-sm btn-primary">
                            <i className="fa fa-plus me-1"></i>Add New
                        </Link>
                    </div>
                </div>
                
                {/* Compact Filters */}
                <div className="card mb-2">
                    <div className="card-body py-2">
                        <div className="row g-2 align-items-end">
                            <div className="col-md-4">
                                <label className="form-label small mb-1">Search</label>
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder="Search name, dept, grade, location..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                            <div className="col-md-3">
                                <label className="form-label small mb-1">Emp Type</label>
                                <Select
                                    options={empTypeOptions}
                                    value={empTypeOptions.find(opt => opt.value === filterEmpType)}
                                    onChange={(opt) => setFilterEmpType(opt ? opt.value : '')}
                                    placeholder="All Types"
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    styles={{
                                        control: (base) => ({ ...base, minHeight: '31px', fontSize: '13px' }),
                                        menuPortal: (base) => ({ ...base, zIndex: 9999 })
                                    }}
                                />
                            </div>
                            <div className="col-md-2">
                                <button className="btn btn-sm btn-outline-secondary" onClick={clearFilters}>
                                    <i className="fa fa-times me-1"></i>Clear
                                </button>
                            </div>
                            <div className="col-md-3 text-end">
                                <span className="badge bg-secondary">{filteredData.length} records</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Table */}
                <div className="card">
                    <div className="card-body p-0">
                        {loading ? (
                            <div className="text-center py-4">
                                <div className="spinner-border" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : filteredData.length === 0 ? (
                            <div className="text-center py-4">
                                <p className="text-muted">No records found</p>
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-hover table-sm mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>#</th>
                                            <th>Employee</th>
                                            <th>Department</th>
                                            <th>Designation</th>
                                            <th>DOJ</th>
                                            <th>Emp Type</th>
                                            <th>Grade</th>
                                            <th>Basic</th>
                                            <th style={{ textAlign: 'center' }}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredData.map((item, index) => (
                                            <tr key={item.id}>
                                                <td>{index + 1}</td>
                                                <td>
                                                    <strong style={{ fontSize: '13px' }}>
                                                        {item.user?.name || 'N/A'} 
                                                        {item.user?.last_name ? ' ' + item.user.last_name : ''}
                                                    </strong>
                                                </td>
                                                <td style={{ fontSize: '12px' }}>
                                                    {item.user?.department?.name || '-'}
                                                </td>
                                                <td style={{ fontSize: '12px' }}>
                                                    {item.designation || '-'}
                                                </td>
                                                <td style={{ fontSize: '12px' }}>
                                                    {item.date_of_join ? moment(item.date_of_join).format('DD MMM YY') : '-'}
                                                </td>
                                                <td>
                                                    <span className={`badge ${item.emp_type === 'Permanent' ? 'bg-success' : item.emp_type === 'Contract' ? 'bg-warning text-dark' : 'bg-secondary'}`} style={{ fontSize: '10px' }}>
                                                        {item.emp_type || '-'}
                                                    </span>
                                                </td>
                                                <td style={{ fontSize: '12px' }}>{item.grade || '-'}</td>
                                                <td style={{ fontSize: '12px' }}>{formatCurrency(item.basic_salary)}</td>
                                                <td className="text-center">
                                                    <div className="btn-group btn-group-sm">
                                                        <button
                                                            className="btn btn-outline-info"
                                                            onClick={() => handleView(item)}
                                                            title="View"
                                                        >
                                                            <i className="fa fa-eye"></i>
                                                        </button>
                                                        <Link 
                                                            to={`/salary_master_edit/${item.id}`} 
                                                            className="btn btn-outline-primary"
                                                            title="Edit"
                                                        >
                                                            <i className="fa fa-edit"></i>
                                                        </Link>
                                                        <button
                                                            className="btn btn-outline-danger"
                                                            onClick={() => handleDelete(item.id)}
                                                            title="Delete"
                                                        >
                                                            <i className="fa fa-trash"></i>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* View Modal */}
            {showViewModal && selectedItem && (
                <div className="modal" tabIndex="-1" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
                        <div className="modal-content">
                            <div className="modal-header" style={{ background: '#f8f9fa' }}>
                                <h6 className="modal-title">
                                    <i className="fa fa-user me-2"></i>
                                    Employee Details - {selectedItem.user?.name || 'N/A'}
                                </h6>
                                <button type="button" className="btn-close" onClick={() => setShowViewModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                {/* Personal Info */}
                                <div className="card mb-3">
                                    <div className="card-header bg-light py-2">
                                        <h6 className="mb-0"><i className="fa fa-user me-2"></i>Personal Information</h6>
                                    </div>
                                    <div className="card-body py-2">
                                        <div className="row">
                                            <div className="col-md-4 mb-2">
                                                <small className="text-muted">Name</small>
                                                <p className="mb-0 fw-semibold">{selectedItem.user?.name} {selectedItem.user?.last_name || ''}</p>
                                            </div>
                                            <div className="col-md-4 mb-2">
                                                <small className="text-muted">Department</small>
                                                <p className="mb-0">{selectedItem.user?.department?.name || '-'}</p>
                                            </div>
                                            <div className="col-md-4 mb-2">
                                                <small className="text-muted">Designation</small>
                                                <p className="mb-0">{selectedItem.designation || '-'}</p>
                                            </div>
                                            <div className="col-md-4 mb-2">
                                                <small className="text-muted">Father Name</small>
                                                <p className="mb-0">{selectedItem.father_name || '-'}</p>
                                            </div>
                                            <div className="col-md-4 mb-2">
                                                <small className="text-muted">Date of Birth</small>
                                                <p className="mb-0">{selectedItem.dob ? moment(selectedItem.dob).format('DD MMM YYYY') : '-'}</p>
                                            </div>
                                            <div className="col-md-4 mb-2">
                                                <small className="text-muted">Date of Joining</small>
                                                <p className="mb-0">{selectedItem.date_of_join ? moment(selectedItem.date_of_join).format('DD MMM YYYY') : '-'}</p>
                                            </div>
                                            {selectedItem.date_of_join && calculateWorkingDuration(selectedItem.date_of_join) && (
                                                <div className="col-md-4 mb-2">
                                                    <small className="text-muted">Working Duration</small>
                                                    <p className="mb-0">
                                                        <span className="badge bg-info text-white">
                                                            <i className="fa fa-clock me-1"></i>
                                                            {calculateWorkingDuration(selectedItem.date_of_join).text}
                                                        </span>
                                                    </p>
                                                </div>
                                            )}
                                            <div className="col-md-4 mb-2">
                                                <small className="text-muted">Qualification</small>
                                                <p className="mb-0">{selectedItem.qualification || '-'}</p>
                                            </div>
                                            <div className="col-md-4 mb-2">
                                                <small className="text-muted">Experience</small>
                                                <p className="mb-0">{selectedItem.experience || '-'}</p>
                                            </div>
                                            <div className="col-md-4 mb-2">
                                                <small className="text-muted">Location</small>
                                                <p className="mb-0">{selectedItem.location || '-'}</p>
                                            </div>
                                            <div className="col-md-4 mb-2">
                                                <small className="text-muted">Old Salary</small>
                                                <p className="mb-0">{formatCurrency(selectedItem.old_salary)}</p>
                                            </div>
                                            <div className="col-md-4 mb-2">
                                                <small className="text-muted">Grade</small>
                                                <p className="mb-0">{selectedItem.grade || '-'}</p>
                                            </div>
                                            <div className="col-md-12 mb-2">
                                                <small className="text-muted">Address</small>
                                                <p className="mb-0">{selectedItem.address || '-'}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Employment Info */}
                                <div className="card mb-3">
                                    <div className="card-header bg-light py-2">
                                        <h6 className="mb-0"><i className="fa fa-briefcase me-2"></i>Employment Details</h6>
                                    </div>
                                    <div className="card-body py-2">
                                        <div className="row">
                                            <div className="col-md-4 mb-2">
                                                <small className="text-muted">Emp Type</small>
                                                <p className="mb-0">
                                                    <span className={`badge ${selectedItem.emp_type === 'Permanent' ? 'bg-success' : 'bg-warning text-dark'}`}>
                                                        {selectedItem.emp_type || '-'}
                                                    </span>
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Bank Info */}
                                <div className="card mb-3">
                                    <div className="card-header bg-light py-2">
                                        <h6 className="mb-0"><i className="fa fa-university me-2"></i>Bank Details</h6>
                                    </div>
                                    <div className="card-body py-2">
                                        <div className="row">
                                            <div className="col-md-4 mb-2">
                                                <small className="text-muted">Bank IFSC</small>
                                                <p className="mb-0">{selectedItem.bank_ifsc || '-'}</p>
                                            </div>
                                            <div className="col-md-4 mb-2">
                                                <small className="text-muted">Account Number</small>
                                                <p className="mb-0">{selectedItem.account_num || '-'}</p>
                                            </div>
                                            <div className="col-md-4 mb-2">
                                                <small className="text-muted">PF Account</small>
                                                <p className="mb-0">{selectedItem.pf_acc_num || '-'}</p>
                                            </div>
                                            <div className="col-md-4 mb-2">
                                                <small className="text-muted">ESI Number</small>
                                                <p className="mb-0">{selectedItem.esi_number || '-'}</p>
                                            </div>
                                        </div>
                                        {/* PF & ESI Contribution */}
                                        <div className="row mt-2">
                                            <div className="col-md-6">
                                                <div className={`p-2 rounded ${selectedItem.pf_enabled ? 'bg-success bg-opacity-10 border border-success' : 'bg-light'}`}>
                                                    <div className="d-flex align-items-center justify-content-between">
                                                        <div>
                                                            <i className={`fa fa-piggy-bank me-2 ${selectedItem.pf_enabled ? 'text-success' : 'text-muted'}`}></i>
                                                            <strong>PF Contribution</strong>
                                                        </div>
                                                        {selectedItem.pf_enabled ? (
                                                            <span className="badge bg-success">{selectedItem.pf_percent || 0}%</span>
                                                        ) : (
                                                            <span className="badge bg-secondary">No</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className={`p-2 rounded ${selectedItem.esi_enabled ? 'bg-primary bg-opacity-10 border border-primary' : 'bg-light'}`}>
                                                    <div className="d-flex align-items-center justify-content-between">
                                                        <div>
                                                            <i className={`fa fa-shield-alt me-2 ${selectedItem.esi_enabled ? 'text-primary' : 'text-muted'}`}></i>
                                                            <strong>ESI Contribution</strong>
                                                        </div>
                                                        {selectedItem.esi_enabled ? (
                                                            <span className="badge bg-primary">{selectedItem.esi_percent || 0}%</span>
                                                        ) : (
                                                            <span className="badge bg-secondary">No</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Salary Info */}
                                <div className="card">
                                    <div className="card-header" style={{ background: '#e8f4fd' }}>
                                        <h6 className="mb-0"><i className="fa fa-money-bill me-2"></i>Salary Details</h6>
                                    </div>
                                    <div className="card-body py-2">
                                        <div className="row g-2">
                                            <div className="col-md-3">
                                                <div className="p-2 border rounded text-center">
                                                    <small className="text-muted d-block">Basic Salary</small>
                                                    <strong className="text-primary">{formatCurrency(selectedItem.basic_salary)}</strong>
                                                </div>
                                            </div>
                                            <div className="col-md-3">
                                                <div className="p-2 border rounded text-center" style={{ background: '#fff3e0' }}>
                                                    <small className="text-muted d-block">Gross Salary</small>
                                                    <strong className="text-warning">{formatCurrency(selectedItem.gross_salary)}</strong>
                                                </div>
                                            </div>
                                            <div className="col-md-3">
                                                <div className="p-2 border rounded text-center" style={{ background: '#e8f5e9' }}>
                                                    <small className="text-muted d-block">Per Day Salary</small>
                                                    <strong className="text-success">{formatCurrency(selectedItem.per_day_salary)}</strong>
                                                </div>
                                            </div>
                                            <div className="col-md-3">
                                                <div className="p-2 border rounded text-center">
                                                    <small className="text-muted d-block">DA %</small>
                                                    <strong className="text-success">{selectedItem.da_per || 0}%</strong>
                                                </div>
                                            </div>
                                            <div className="col-md-3">
                                                <div className="p-2 border rounded text-center">
                                                    <small className="text-muted d-block">HRA %</small>
                                                    <strong className="text-success">{selectedItem.hra_per || 0}%</strong>
                                                </div>
                                            </div>
                                            <div className="col-md-3">
                                                <div className="p-2 border rounded text-center">
                                                    <small className="text-muted d-block">TDS %</small>
                                                    <strong className="text-danger">{selectedItem.tds_per || 0}%</strong>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="mt-2 p-2 bg-light rounded">
                                            <small className="text-muted">Formula: </small>
                                            <code>{selectedItem.formula || 'A+B+C-D'}</code>
                                            <small className="text-muted ms-2">(A=Basic, B=DA, C=HRA, D=TDS)</small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer py-2">
                                <Link to={`/salary_master_edit/${selectedItem.id}`} className="btn btn-sm btn-primary">
                                    <i className="fa fa-edit me-1"></i>Edit
                                </Link>
                                <button type="button" className="btn btn-sm btn-secondary" onClick={() => setShowViewModal(false)}>
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
